// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/OsImageSlot.tsx
//  Slot de foto da OS (tarefa antes/depois, despesa). Exibe via blob autenticado
//  (apiClient → objectURL, pois <img src> não envia o token).
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Trash2, Upload, Image as ImageIcon } from 'lucide-react';

interface Props {
  /** Caminho da imagem na API (ex.: /api/os/1/tarefas/2/imagem/inicio). */
  path: string;
  label: string;
  hasImage: boolean;
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
}

export function OsImageSlot({ path, label, hasImage, disabled, onUpload, onRemove }: Props) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const { data: blobUrl } = useQuery({
    queryKey: ['os-img', path],
    queryFn: async () => {
      const res = await apiClient.get(path, { responseType: 'blob' });
      return URL.createObjectURL(res.data as Blob);
    },
    enabled: hasImage,
    staleTime: Infinity,
  });

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const handleUpload = async (file: File) => {
    setBusy(true);
    try {
      await onUpload(file);
      await qc.invalidateQueries({ queryKey: ['os-img', path] });
    } finally { setBusy(false); }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await onRemove();
      await qc.invalidateQueries({ queryKey: ['os-img', path] });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-1.5 w-32">
      <p className="text-xs font-medium">{label}</p>
      <div className="relative aspect-square rounded-lg border border-dashed flex items-center justify-center bg-muted/30 overflow-hidden">
        {hasImage && blobUrl ? (
          <img src={blobUrl} alt={label} className="w-full h-full object-contain" />
        ) : (
          <div className="text-center text-muted-foreground">
            <ImageIcon className="h-7 w-7 mx-auto opacity-40" />
            <span className="text-xs">Sem foto</span>
          </div>
        )}
      </div>
      {!disabled && (
        <div className="flex gap-1">
          <Button type="button" variant="outline" size="sm" className="flex-1 h-7 text-xs"
            disabled={busy} onClick={() => inputRef.current?.click()}>
            <Upload className="h-3 w-3 mr-1" />{hasImage ? 'Trocar' : 'Enviar'}
          </Button>
          {hasImage && (
            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive"
              disabled={busy} onClick={handleRemove} title="Remover">
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ''; }} />
    </div>
  );
}
